import { inject, Injectable, signal } from "@angular/core";
import { EnergyToolExtractionService } from "../../../../../services/state/energy-tool-extraction/energy-tool-extraction.service";
import { TelemetryService } from "../../../../../services/api/telemetry/telemetry.service";
import { EnergyToolUtilsService } from "../../../../../services/utils/energy-tool-utils/energy-tool-utils.service";
import { ASSET_TYPES } from "../../../../../enums/assetTypes.enum";
import { DateTime, DateTimeUnit } from "luxon";
import { forkJoin } from "rxjs";
import { Location } from "../../../../../models/api/asset/location";
import { EnergyToolUpdateService } from "../../../../../services/state/energy-tool-update/energy-tool-update.service";
import { AssetService } from "../../../../../services/api/asset/asset.service";
import { DoughnutChartData } from "../../../../../models/ui/chartData";
import { BarChartData, LineChartData } from "../../../../../models/ui/energyToolCharts";
import { ForecastEnergyLoss } from "../../../../../models/ui/weatherForecast";
import { isNotNullOrUndefined } from "../../../../../shared/utils/utils";
import { ForecastTelemetryEntry } from "../../../../../models/shared/telemetry";

@Injectable()
export class LocationEnergyToolService {
  // State signals for Location Energy Tool UI:
  private readonly _barChartEnergyLoss = signal<BarChartData[] | null>(null);
  readonly barChartEnergyLoss = this._barChartEnergyLoss.asReadonly();

  private readonly _lineChartEnergyLoss = signal<LineChartData[] | null>(null);
  readonly lineChartEnergyLoss = this._lineChartEnergyLoss.asReadonly();

  private readonly _doughnutChartEnergyLoss = signal<DoughnutChartData[] | null>(null);
  readonly doughnutChartEnergyLoss = this._doughnutChartEnergyLoss.asReadonly();

  private readonly _energyLoss = signal<number | null>(null);
  readonly energyLoss = this._energyLoss.asReadonly();

  private readonly _energyCost = signal<number | null>(null);
  readonly energyCost = this._energyCost.asReadonly();

  private readonly _forecastWeather = signal<ForecastTelemetryEntry | null | undefined>(null);
  readonly forecastWeather = this._forecastWeather.asReadonly();

  private readonly _forecastEnergyLoss = signal<ForecastEnergyLoss | null | undefined>(null);
  readonly forecastEnergyLoss = this._forecastEnergyLoss.asReadonly();

  private readonly _expectedEnergyLossPerDay = signal<number | null>(null);
  readonly expectedEnergyLossPerDay = this._expectedEnergyLossPerDay.asReadonly();

  private telemetryService = inject(TelemetryService);
  private assetService = inject(AssetService);
  private energyExtraction = inject(EnergyToolExtractionService);
  private energyUpdate = inject(EnergyToolUpdateService);
  private energyUtils = inject(EnergyToolUtilsService);

  /**
   * Resets all internal state signals related to energy loss,
   * cost, and forecasts to their initial null state.
   */

  private resetStates() {
    this._barChartEnergyLoss.set(null);
    this._lineChartEnergyLoss.set(null);
    this._energyLoss.set(null);
    this._doughnutChartEnergyLoss.set(null);
    this._energyCost.set(null);
    this._forecastWeather.set(null);
    this._forecastEnergyLoss.set(null);
    this._expectedEnergyLossPerDay.set(null);
  }

  /**
   * Loads initial state of the Location Energy Tool.
   * @param locationId - The ID of the Location asset.
   */
  public loadInitialState(locationId: string) {
    this.resetStates();
    const now = DateTime.now().toMillis();
    const startOfMonth = DateTime.now().startOf("month").toMillis();

    // Preparing all needed observables
    const locationEnergyLoss$ = this.fetchLocationEnergyLoss(locationId, startOfMonth, now);

    const childrenEnergyLoss = this.fetchChildrenEnergyLoss(locationId, startOfMonth, now);

    const locationTelemetry$ = this.fetchLocationTelemetry(locationId);

    const locationAsset$ = this.fetchLocationAsset(locationId);

    const forecast$ = this.telemetryService.getAttributes(locationId, ASSET_TYPES.LOCATION, "forecast_weather_data");

    //Subscription:
    forkJoin({
      locationEnergyLoss: locationEnergyLoss$,
      childrenEnergyLoss: childrenEnergyLoss,
      locationAsset: locationAsset$,
      forecast: forecast$,
      locationTelemetry: locationTelemetry$,
    }).subscribe({
      next: (res) => {
        const childrenEnergyLoss = res.childrenEnergyLoss as any[];
        const locationEnergyLoss = res.locationEnergyLoss;
        const location = res.locationAsset as Location;
        const forecastData = res.forecast?.forecast_weather_data;
        const fromLocation = res.locationTelemetry;

        this._forecastWeather.set(forecastData);

        if (
          isNotNullOrUndefined(fromLocation.forecast_energy_loss_today?.[0]?.value) &&
          isNotNullOrUndefined(fromLocation.forecast_energy_loss_tomorrow?.[0]?.value) &&
          isNotNullOrUndefined(fromLocation.forecast_energy_loss_day_after_tomorrow?.[0]?.value)
        ) {
          this._forecastEnergyLoss.set({
            today: fromLocation.forecast_energy_loss_today[0].value,
            tomorrow: fromLocation.forecast_energy_loss_tomorrow[0].value,
            dayAfterTomorrow: fromLocation.forecast_energy_loss_day_after_tomorrow[0].value,
          });
        }

        if (childrenEnergyLoss) {
          this.energyExtraction.extractAndSetBarChartData(
            childrenEnergyLoss,
            false,
            startOfMonth,
            "month",
            this._barChartEnergyLoss.set.bind(this._barChartEnergyLoss)
          );
        }

        if (locationEnergyLoss?.energy_loss && locationEnergyLoss?.normal_energy_loss) {
          this.energyExtraction.extractAndSetLineChartData(
            locationEnergyLoss?.energy_loss,
            locationEnergyLoss?.normal_energy_loss,
            startOfMonth,
            "month",
            this._lineChartEnergyLoss.set.bind(this._lineChartEnergyLoss)
          );
        }
        this.energyExtraction.extractAndSetTotalEnergyLoss(
          locationEnergyLoss,
          "today",
          this._energyLoss.set.bind(this._energyLoss)
        );

        this.energyExtraction.extractAndSetEnergyCost(
          locationEnergyLoss,
          "today",
          this._energyCost.set.bind(this._energyCost)
        );
        this.energyExtraction.extractAndSetExpectedEnergyLossPerDay(
          location,
          this._expectedEnergyLossPerDay.set.bind(this._expectedEnergyLossPerDay)
        );

        this.energyExtraction.extractAndSetDoughnutChart(
          childrenEnergyLoss,
          this._doughnutChartEnergyLoss.set.bind(this._doughnutChartEnergyLoss)
        );
      },
    });
  }

  /**
   * Updates energy loss for a given time window.
   * @param locationId - The ID of the Location asset.
   * @param timeWindow - The time window to update energy loss for.
   */
  public updateEnergyLossForTimeWindow(locationId: string, timeWindow: string) {
    this.energyUpdate.updateEnergyLossForTimeWindow(
      locationId,
      timeWindow,
      this._energyLoss.set.bind(this._energyLoss),
      this._energyCost.set.bind(this._energyCost),
      this.fetchLocationEnergyLoss.bind(this)
    );
  }

  /**
   * Updates bar chart data for a given time range.
   * @param start - The start of the time range.
   * @param end - The end of the time range.
   * @param timeFrame - The time frame to update bar chart data for.
   * @param locationId - The ID of the Location asset.
   * @param assetLabel - The label of the asset to update bar chart data for.
   */
  public updateBarChartForTimeRange(
    start: number,
    end: number,
    timeFrame: DateTimeUnit,
    locationId: string,
    assetLabel: string | undefined
  ) {
    this.energyUpdate.updateBarChartForTimeWindow(
      locationId,
      assetLabel,
      start,
      end,
      timeFrame,
      false,
      false,
      this._barChartEnergyLoss.set.bind(this._barChartEnergyLoss),
      this.fetchChildrenEnergyLoss.bind(this)
    );
  }

  /**
   * Updates doughnut chart data for a given time range.
   * @param locationId - The ID of the Location asset.
   * @param timeFrame - The time frame to update doughnut chart data for.
   */
  public updateDoughnutChartForTimeRange(locationId: string, timeFrame: string) {
    this.energyUpdate.updateDoughnutChartForTimeWindow(
      locationId,
      timeFrame,
      this._doughnutChartEnergyLoss.set.bind(this._doughnutChartEnergyLoss),
      this.fetchChildrenEnergyLoss.bind(this)
    );
  }

  /**
   * Updates line chart data for a given time range.
   * @param start - The start of the time range.
   * @param end - The end of the time range.
   * @param timeFrame - The time frame to update line chart data for.
   * @param locationId - The ID of the Location asset.
   */
  public updateLineChartForTimeRange(start: number, end: number, timeFrame: DateTimeUnit, locationId: string) {
    this.energyUpdate.updateLineChartForTimeWindow(
      locationId,
      start,
      end,
      timeFrame,
      this._lineChartEnergyLoss.set.bind(this._lineChartEnergyLoss),
      this.fetchLocationEnergyLoss.bind(this)
    );
  }

  /**
   * Fetches children energy loss data.
   * @param locationId - The ID of the Location asset.
   * @param start - The start time.
   * @param end - The end time.
   * @returns An observable of the children energy loss data.
   */
  private fetchChildrenEnergyLoss(locationId: string, start: number, end: number) {
    return this.energyUtils.getChildrenEnergyLossObservable(locationId, ASSET_TYPES.LOCATION, start, end);
  }

  /**
   * Fetches location telemetry data.
   * @param locationId - The ID of the Location asset.
   * @returns An observable of the telemetry data.
   */
  private fetchLocationTelemetry(locationId: string) {
    return this.telemetryService.getTimeseries(
      locationId,
      ASSET_TYPES.LOCATION,
      "forecast_weather_data,forecast_energy_loss_today,forecast_energy_loss_tomorrow,forecast_energy_loss_day_after_tomorrow"
    );
  }

  /**
   * Fetches location asset details.
   * @param locationId - The ID of the Location asset.
   */
  private fetchLocationAsset(locationId: string) {
    return this.assetService.getAssetDetails(locationId, ASSET_TYPES.LOCATION);
  }

  /**
   * Fetches location energy loss data.
   * @param locationId - The ID of the Location asset.
   * @param start - The start time.
   * @param end - The end time.
   * @returns An observable of the energy loss data.
   */
  private fetchLocationEnergyLoss(locationId: string, start: number, end: number) {
    return this.telemetryService.getTimeseries(
      locationId,
      ASSET_TYPES.LOCATION,
      "energy_loss,normal_energy_loss,energy_loss_costs",
      start,
      end
    );
  }
}
